import type { ReactNode } from 'react';
import { StatusLamp } from './StatusLamp.tsx';
import type { LampTone } from './StatusLamp.tsx';
import { cx } from './cx.ts';

/**
 * Status chip: a tinted pill with an optional ink dot and tracked
 * mono-label text (DESIGN.md §6). English-only surface, so uppercase
 * tracking is the house style for chip text.
 */
export type ChipTone = 'ok' | 'warn' | 'bad' | 'rust' | 'neutral';

const TONE_CLASSES: Record<ChipTone, string> = {
  ok: 'border-ok-line bg-ok-tint text-forest-deep',
  warn: 'border-warn-line bg-warn-tint text-warn',
  bad: 'border-bad-line bg-bad-tint text-bad',
  rust: 'border-rust-line bg-rust-tint text-rust-text',
  neutral: 'border-line bg-transparent text-ink-low',
};

const LAMP_TONE: Record<ChipTone, LampTone> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
  rust: 'rust',
  neutral: 'off',
};

interface Props {
  readonly tone: ChipTone;
  readonly children: ReactNode;
  readonly lamp?: boolean;
  readonly pulse?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function Chip({ tone, children, lamp = true, pulse = false, className, testId }: Props): JSX.Element {
  return (
    <span
      data-testid={testId}
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono text-mono-label uppercase',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {lamp ? <StatusLamp tone={LAMP_TONE[tone]} size="sm" pulse={pulse} /> : null}
      {children}
    </span>
  );
}
